import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      // Backend always returns 200 (no enumeration) — we just toast + flip to sent state
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success(data?.message || "Check your inbox.");
    } catch (err) {
      // Show a helpful diagnostic rather than the generic fallback. Common failure
      // modes: 404 (endpoint not deployed yet), 422 (malformed email), network error.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const detailMsg = Array.isArray(detail) ? detail[0]?.msg : detail;
      let msg;
      if (status === 404) msg = "Reset is not available on this server yet — please try again in a moment.";
      else if (status === 422) msg = detailMsg || "Please enter a valid email address.";
      else if (!err?.response) msg = "Network error — check your connection and retry.";
      else msg = detailMsg || `Could not send reset link (HTTP ${status || "?"})`;
      console.error("[forgot-password] failed", { status, detail });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative">
      <img
        src="https://images.unsplash.com/photo-1759842952767-456f1b6dded8?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
        alt=""
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/85 to-[#0A0A0A]" />
      <header className="relative z-10 px-6 lg:px-12 py-6">
        <Link to="/" className="text-[#E50914] font-black text-2xl">CINECINEMA</Link>
      </header>
      <main className="relative z-10 max-w-md mx-auto px-6 py-12">
        <div
          data-testid="forgot-password-card"
          className="bg-black/70 backdrop-blur-xl rounded-lg p-10 border border-white/5 shadow-2xl"
        >
          {!sent ? (
            <>
              <h1 className="text-3xl font-bold mb-2">Forgot password?</h1>
              <p className="text-sm text-gray-400 mb-6">
                Enter your account email and we&apos;ll send you a one-time link to set a new password. The link expires in 15 minutes.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <input
                  data-testid="forgot-password-email"
                  type="email"
                  required
                  placeholder="Email address"
                  className="input-dark"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button
                  data-testid="forgot-password-submit"
                  type="submit"
                  disabled={loading}
                  className="btn-red w-full py-3 rounded font-semibold"
                >
                  {loading ? "Sending..." : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-password-sent" className="text-center">
              <div className="w-14 h-14 bg-[#E50914]/10 border border-[#E50914]/40 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E50914" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2">Check your inbox</h2>
              <p className="text-sm text-gray-400">
                If an account exists for <strong className="text-white">{email}</strong>, we&apos;ve sent a reset link.
                It expires in 15 minutes. Be sure to check spam.
              </p>
            </div>
          )}
          <p className="mt-8 text-gray-400 text-sm text-center">
            <Link to="/login" className="text-white hover:underline" data-testid="back-to-login-link">
              ← Back to sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
